import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { AnimatePresence } from "framer-motion";
import FilePreview from "@rapid/components/file/Preview";
import { FileCarousel } from "@rapid/components/file/Carousel/FileCarousel";
import { AnnotationEditRequestDetails } from "@rapid/utils/annotationStrategies/types";
import {
    BoundingBox,
    BoundingBoxStrategyImpl
} from "@rapid/utils/annotationStrategies/BoundingBox";
import AnnotationLabelInput from "@rapid/components/annotation/AnnotationLabelInput";
import { useMedia } from "@rapid/contexts/MediaContext";
import { useCoreApp } from "@rapid/contexts/CoreAppContext";
import { useFlow } from "@rapid/contexts/FlowContext";
import { useFrame } from "@rapid/contexts/FrameContext";
import { useComputedValues } from "@rapid/contexts/ComputedValuesContext";
import { FilePreviewProvider } from "@rapid/contexts/FilePreviewProvider";
import { Annotation } from "@rapid/types/annotation";
import { BuildSteps } from "@rapid/types/flows";
import { useFlowState } from "@rapid/hooks/useFlowState";
import {
    useCurrentMedia,
    usePromptLabels,
    useUpdatePromptLabels,
    useSyncPromptLabels
} from "@rapid/store";
import { useTutorial } from "@rapid/hooks/useTutorial";
import { DEFAULT_LABEL } from "@rapid/components/annotation/colors";

export const PromptVisualAreaContent = () => {
    const { addMediaItems, orderedMediaItems } = useMedia();
    const currentMedia = useCurrentMedia();
    const { showLabels, setPreviouslySelectedObject } = useCoreApp();
    const { setCurrentFrameAnnotations } = useFlow();
    const {
        selectedAnnotationId,
        setSelectedAnnotationId,
        shouldShowAnnotationInput,
        setShouldShowAnnotationInput
    } = useFrame();
    const { currentFrameAnnotations, currentFramePredictions, allAnnotationsAcrossFiles } =
        useComputedValues();
    const promptLabels = usePromptLabels();
    const updatePromptLabels = useUpdatePromptLabels();
    const syncPromptLabels = useSyncPromptLabels();
    const { step } = useFlowState();
    const { openFirstTime } = useTutorial();

    const visualAreaContainerRef = useRef<HTMLDivElement>(null);
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        openFirstTime();
    }, [openFirstTime]);
    const [editingAnnotationDetails, setEditingAnnotationDetails] =
        useState<AnnotationEditRequestDetails<BoundingBox> | null>(null);
    const pendingLabelRef = useRef<{ annotationId: string; label: string } | null>(null);

    const onFileChange = useCallback(() => {
        setEditingAnnotationDetails(null);
        pendingLabelRef.current = null;
    }, []);

    const handleAnnotationEditRequest = useCallback(
        (details: AnnotationEditRequestDetails<BoundingBox>) => {
            setEditingAnnotationDetails(details);
        },
        []
    );

    const getAnnotationLabel = useCallback(
        (annotationId: string) => {
            return currentFrameAnnotations.find((anno) => anno.id === annotationId)?.label;
        },
        [currentFrameAnnotations]
    );

    const cleanupDefaultLabel = useCallback(
        (annotationId: string, oldLabel: string | undefined, newLabel: string) => {
            if (oldLabel === DEFAULT_LABEL && oldLabel !== newLabel) {
                const hasOtherAnnotationsWithDefaultLabel = allAnnotationsAcrossFiles.some(
                    (anno) => anno.label === DEFAULT_LABEL && anno.id !== annotationId
                );

                if (!hasOtherAnnotationsWithDefaultLabel) {
                    const remainingLabelNames = Array.from(promptLabels.keys()).filter(
                        (labelName) => labelName !== DEFAULT_LABEL
                    );

                    updatePromptLabels([], [DEFAULT_LABEL]);

                    setTimeout(() => {
                        syncPromptLabels([...remainingLabelNames, newLabel]);
                    }, 0);
                }
            }
        },
        [allAnnotationsAcrossFiles, promptLabels, updatePromptLabels, syncPromptLabels]
    );

    const updateAnnotationLabel = useCallback(
        (annotationId: string, newLabel: string) => {
            const sanitizedLabel = newLabel.trim().toLowerCase();
            if (!sanitizedLabel) return;

            const oldLabel = getAnnotationLabel(annotationId);

            setPreviouslySelectedObject(sanitizedLabel);
            setCurrentFrameAnnotations((prevAnnotations: Annotation[]) =>
                (prevAnnotations || []).map((anno: Annotation) =>
                    anno.id === annotationId
                        ? { ...anno, label: sanitizedLabel, draft: false, modifiedAt: Date.now() }
                        : anno
                )
            );

            cleanupDefaultLabel(annotationId, oldLabel, sanitizedLabel);
        },
        [
            getAnnotationLabel,
            setPreviouslySelectedObject,
            setCurrentFrameAnnotations,
            cleanupDefaultLabel
        ]
    );

    const handleLabelInputClose = useCallback(
        (shouldDiscard: boolean = false) => {
            if (!shouldDiscard && pendingLabelRef.current) {
                const { annotationId, label } = pendingLabelRef.current;
                updateAnnotationLabel(annotationId, label);
            }
            pendingLabelRef.current = null;

            setEditingAnnotationDetails(null);
            setSelectedAnnotationId(null);
            setShouldShowAnnotationInput(false);

            if (editingAnnotationDetails) {
                setCurrentFrameAnnotations((prevAnnotations: Annotation[]) =>
                    (prevAnnotations || []).filter((anno: Annotation) => !anno.empty)
                );
            }
        },
        [
            editingAnnotationDetails,
            setCurrentFrameAnnotations,
            setSelectedAnnotationId,
            setShouldShowAnnotationInput,
            updateAnnotationLabel
        ]
    );

    const handleCanvasClick = useCallback(() => {
        handleLabelInputClose();
    }, [handleLabelInputClose]);

    const handleLabelChangedByInput = useCallback(
        (annotationId: string, newLabel: string) => {
            updateAnnotationLabel(annotationId, newLabel);
        },
        [updateAnnotationLabel]
    );

    const handlePendingLabelChange = useCallback((annotationId: string, newLabel: string) => {
        pendingLabelRef.current = { annotationId, label: newLabel };
    }, []);

    const onAddMediaItems = useCallback(
        async (files: File[], onFinally?: () => void) => {
            const result = await addMediaItems(files);

            // Close modal if validation passed (non-empty result)
            // Empty result means validation failed - keep modal open to show error
            if (result && result.length > 0) {
                onFinally?.();
            }
        },
        [addMediaItems]
    );

    // Show input on label click or thumbnail click
    useEffect(() => {
        if (
            selectedAnnotationId &&
            shouldShowAnnotationInput &&
            currentMedia?.frame?.id &&
            visualAreaContainerRef.current &&
            !editingAnnotationDetails // Only trigger if not already editing (to avoid interfering with drawing flow)
        ) {
            // Find the selected annotation in the current frame
            const selectedAnnotation = currentFrameAnnotations.find(
                (anno: BoundingBox) => anno.id === selectedAnnotationId
            );

            if (selectedAnnotation) {
                // Find the actual canvas element to get its bounding rect
                const canvasElement = visualAreaContainerRef.current.querySelector(
                    "[data-annotation-canvas]"
                ) as HTMLCanvasElement;
                const canvasBoundingRect =
                    canvasElement?.getBoundingClientRect() ||
                    visualAreaContainerRef.current.getBoundingClientRect();

                // Create AnnotationEditRequestDetails for the canvas input
                const boxTlxCanvas = selectedAnnotation.x - selectedAnnotation.width / 2;
                const boxTlyCanvas = selectedAnnotation.y - selectedAnnotation.height / 2;

                const editDetails: AnnotationEditRequestDetails<BoundingBox> = {
                    annotation: selectedAnnotation,
                    boxCanvasTop: boxTlyCanvas,
                    boxCanvasLeft: boxTlxCanvas,
                    canvasBoundingRect: canvasBoundingRect
                };

                setEditingAnnotationDetails(editDetails);
            }
        }
    }, [
        selectedAnnotationId,
        shouldShowAnnotationInput,
        currentMedia?.frame?.id,
        currentFrameAnnotations,
        editingAnnotationDetails
    ]);

    return (
        <FilePreviewProvider onFileChange={onFileChange}>
            <AnimatePresence mode="wait">
                <div
                    ref={visualAreaContainerRef}
                    className="visualArea"
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    <div className="flex min-h-0 flex-1">
                        <FileCarousel onAddMediaItems={onAddMediaItems} isHovered={isHovered}>
                            <FilePreview
                                key={`file-${currentMedia?.file.id}`}
                                strategy={BoundingBoxStrategyImpl}
                                forceHideLabels={!showLabels}
                                editingAnnotationId={
                                    editingAnnotationDetails?.annotation.id || null
                                }
                                onAnnotationEditRequest={handleAnnotationEditRequest}
                                onCanvasClickWithoutAnnotation={handleCanvasClick}
                            />
                        </FileCarousel>
                    </div>

                    {editingAnnotationDetails && visualAreaContainerRef.current && (
                        <AnnotationLabelInput
                            key={`inner-input-${editingAnnotationDetails.annotation.id}`}
                            annotationEditRequestDetails={editingAnnotationDetails}
                            containerRef={visualAreaContainerRef as React.RefObject<HTMLDivElement>}
                            onLabelChange={handleLabelChangedByInput}
                            onPendingLabelChange={handlePendingLabelChange}
                            onClose={handleLabelInputClose}
                        />
                    )}
                </div>
            </AnimatePresence>
        </FilePreviewProvider>
    );
};
